import urlJoin from "url-join";
import objectAssignDeep from "./tools/object-assign-deep";

/**
 * @class
 * @classdesc Handle 3D scene view
 */
export default class HcsCad
{
	constructor(paramObj) {
		this.hcs = paramObj.hcs;

		this.initialized = false;

		this.ccAO = null;

		let cameraOptionsDb = this.hcs.settings.get("SETTINGS.CCAO_CAMERA_SETTINGS", "json") || {};

		this.cameraOptions = objectAssignDeep({},
			{
				cameraId: undefined,
				cameraPos: { x: 4, y: -10, z: 4 },
				fitZoom: true,
				zoom: 0.1,
				minZoom: 0,
				maxZoom: "Infinity",
				target: {x: 0, y: 0, z: 0}
			}, cameraOptionsDb);

		this.instanceProgressLoaders = {};

		this.sceneResizedEvents = [];

		this.dragItemStateChangedEvents = [];

		this.initialize();
	}

	initialize() {
		let self = this;
		let containerId = "hcs-scene-" + this.hcs.configurator;

		this.webGlContainer = this.hcs.api.instance(".hcs-scene");
		this.webGlContainer.id = containerId;

		this.ccAO = new CCAssemblerOnline();

		let ccCatalogUrl = this.hcs.settings.get("SETTINGS.CAD_SERVER");
		let ccCatalogUrlWsa = this.hcs.settings.get("SETTINGS.CAD_SERVER_WSA");

		this.ccAO.Options.wsaTo = ccCatalogUrlWsa || ccCatalogUrl;
		this.ccAO.Options.ccCatalogURL = ccCatalogUrl;
		this.progressLoading = false;

		/*
		 * The following 2 options will be set when a new configuration has been started.
		 * We use the ID which was delivered by Haefele shop.
		 * See api.startConfiguration() for details.
		 * 
		 * this.ccAO.Options.sessionId = xxx;
		 * this.ccAO.Options.configId = xxx;
		 */

		this.ccAO.Options.licenseKey = this.hcs.settings.get("SETTINGS.CAD_SERVER_LICENSE_KEY");

		this.ccAO.Options.cultureId = this.hcs.cultureId;
		this.ccAO.Options.pathToCCAO = urlJoin(this.hcs.constants.appUrl, "/ccAssemblerOnline/");
		this.ccAO.Options.pathToCCViewer = urlJoin(this.hcs.constants.appUrl, "/ccAssemblerOnline/build/ccViewerWebGL/build/");
		this.ccAO.Options.pathToTextures = "/CAD/Includes/Texturen/";

		this.ccAO.Options.viewerConfig.containerID = containerId;
		this.ccAO.Options.viewerConfig.viewerWidth = this.webGlContainer.offsetWidth;
		this.ccAO.Options.viewerConfig.viewerHeight = this.webGlContainer.offsetHeight;

		// We don't use static settings here since they may be different for each configurator.
		// Instead change CCAO configuration in database using the key: SETTINGS.CCAO_OPTIONS
		let viewerOptions = this.hcs.settings.get("SETTINGS.CCAO_OPTIONS", "json");

		objectAssignDeep(this.ccAO.Options, viewerOptions);

		window.addEventListener(
			"message",
			(event) => {
				let itemData = event.data.messageData;

				switch(event.data.messageType) {
					case "ccAO.init":
						self.initialized = true;
						self.autoResize();
						break;

					case "ccAO.loading":
						if(!itemData.loader && Object.keys(this.instanceProgressLoaders).length < 1) {

							setTimeout(() => {
								if(this.progressLoading) {
									self.hcs.api.progressBar.progress("indeterminate");
								}
							}, 500);

							this.progressLoading = true;
						}
						break;

					case "ccAO.loaded":
						if(itemData.loader && this.instanceProgressLoaders[itemData.loader.jobID]) {
							this.instanceProgressLoaders[itemData.loader.jobID].loaded++;

							self.hcs.api.progressBar.progress((this.instanceProgressLoaders[itemData.loader.jobID].loaded / this.instanceProgressLoaders[itemData.loader.jobID].size));
						} else {
							self.hcs.api.progressBar.progress(false);
							this.progressLoading = false;
						}
						break;

					case "ccAO.list_loaded":
						// clear queue and hide progress bar when finished.
						if(itemData.loader && this.instanceProgressLoaders[itemData.loader.jobID]) {
							self.hcs.api.progressBar.progress(false);
							this.progressLoading = false;
							delete this.instanceProgressLoaders[itemData.loader.jobID];

							switch(itemData.loader.jobID) {
								case "loadConfiguration":
									self.sceneReset();
									break;
							}
						}
						break;

					case "ccAO.anchorClick":
						self.fireDragItemStateChanged(event.data.messageType);
						break;

					case "ccAO.noIntersectClick":
						self.fireDragItemStateChanged(event.data.messageType);
						break;
				}
			},
			false
		);

		this.ccAO.init(this.ccAO.Options);

		// Exit current cCAO item placement when to user clicks inside the configurator or whole document.
		// We exclude the scene itself as well as the drag items in config area since this will lead to errors otherwise.
		document.addEventListener("click", (event) => {
			let eventPath = self.hcs.api.eventPath(event);

			if(!eventPath.find(element => element.classList && (element.classList.contains("hcs-dragitem") || element.classList.contains("hcs-scene") || element.classList.contains("ccao-btn-wrapper"))) && self.initialized) {
				setTimeout(() => {
					self.ccAO.Snap.resetInteraction("move");
					self.fireDragItemStateChanged("ccAO.resetInteraction");
				}, 100);
			}
		});
	}

	/**
	 * Load instance list and start progress bar showing the current state of loading list of instances.
	 * Use this for long running tasks instead of direct function in ccAO.
	 * @param {Object} instanceList - Instance load list.
	 */
	loadInstanceList(instanceList) {
		this.hcs.api.progressBar.progress("indeterminate");

		this.instanceProgressLoaders[instanceList.jobID] = {
			size: instanceList.instanceArr.length,
			loaded: 0
		};

		this.ccAO.loadInstanceList(instanceList);
	}

	/**
	 * Returns CAD build instructions.
	 * @param {Object} [options] - Options
	 * @returns {string} Build instructions as CADClick XML.
	 */
	getBuildInstructions(options) {
		let buildInstruction = new this.ccAO.BuildInstruction({ version: 2 });
		buildInstruction.readInstancesFromScene({});

		return buildInstruction.getXMLStr(Object.assign({}, {
			format: "relative",
			SOAP: false
		}, options));
	}

	cadBox() {
		//if( !this.checkFunction("configSave", true, "WarningSaveLocked") ) { return; }
        if( !this.hcs.api.checkAuthentication() ) { return; }
		if( !this.hcs.api.checkFunction("cadBox") ) { return; }

		this.hcs.api.logToolbarOption("CadBox");

		let cadBoxContainer = this.hcs.api.instance(".hcs-cadbox");
		let overlayContainer = this.hcs.api.instance(".hcs-background-main");

		cadBoxContainer.querySelector(".hcs-close").addEventListener("click", () => {
			cadBoxContainer.classList.remove("hcs-show");
			overlayContainer.classList.remove("hcs-show");
			cadBoxContainer.querySelector("iframe").src = "about:blank";
		}, { once: true });

		this.hcs.api.callback({
			url: this.hcs.api.getUrl(`xmltransform/${this.hcs.cultureId}/${this.hcs.configurationId}/CadBox`),
			data: {
				xmlContent: this.getBuildInstructions(),
				xsltVariables: this.hcs.cfgEngine.cadProcessorXsltVariables("cadbox")
			},
			method: "POST",
			lockUi: true
		}).then(result => {
			if(result.success) {
				cadBoxContainer.classList.add("hcs-show");
				overlayContainer.classList.add("hcs-show");

				cadBoxContainer.querySelector("textarea[name='XmlContent']").value = result.xmlContent;
				cadBoxContainer.querySelector("form").submit();
			}
		});
	}

	/**
	 * Create dimensional drawing.
	 */
	exportDrawing() {
        if( !this.hcs.api.checkAuthentication() ) { return; }
		if( !this.hcs.api.checkFunction("dimensionalDrawing") ) { return; }

		this.hcs.api.logToolbarOption("Dimensional Drawing");

		let self = this;
		let loading = true;

		setTimeout(function() {
			if(loading) {
				self.hcs.api.progressBar.progress("indeterminate");
			}
		}, 200);

		let windowReference = null;

		if(this.hcs.ui.browser.os.name === "iOS" && ["mobile", "tablet"].includes(this.hcs.ui.browser.platform.type)) {
			windowReference = window.open();
			windowReference.location = this.hcs.api.getUrl("FileGenerator", "HCS_DRAWING", this.hcs.sectionId, this.hcs.cultureId);
		}

		this.hcs.api.saveBuildInstructions({
			success: function(result) {
				loading = false;
				self.hcs.api.progressBar.progress(false);

				if(result.success) {
					self.hcs.api.callback({
						url: self.hcs.api.getUrl("cad", self.hcs.cultureId, self.hcs.configurationId, "DimensionalDrawing"),
						data: {
							xsltVariables: this.hcs.cfgEngine.cadProcessorXsltVariables("dimsheet")
						},
						method: "POST",
						lockUi: true
					}).then(result => {
						if(result.success) {
							let dlItem = result.files.filter(f => { return (f.fileExtension || f.extension) === ".pdf"; })[0];

							if(self.hcs.ui.browser.os.name === "iOS" && ["mobile", "tablet"].includes(self.hcs.ui.browser.platform.type)) {
								windowReference.location = dlItem.url;
							} else {
								self.hcs.api.downloadFile(dlItem.url, { filename: dlItem.fileName.substring(0, dlItem.fileName.lastIndexOf(".") - 1) });
							}
						}
					});
				}
			}
		});
	}

	sceneReset(options) {
		let self = this;

		if(this.ccAO === null || !this.initialized) {
			return;
		}

		let opts = Object.assign({
			updateBoundingBox: false,
			animate: true
		}, options);

		this.hcs.cfgEngine.adjustCamera(opts).then(continueCameraAdjust => {
			if(typeof (continueCameraAdjust) === "undefined") {
				console.warn("Function adjustCamera() in entry.js does not correctly handle the returning Promise, which requires being resolved with either 'true' or 'false'!");
			}

			if(continueCameraAdjust) {
				commonCameraAdjust.call(self);
			}
		});

		let commonCameraAdjust = _ => {
			if(opts.updateBoundingBox) {
				let sceneBBoxWithoutAnchorPrev = this.ccAO.getSceneBoundingBoxWithoutAnchorPreviews();
				this.ccAO.ccViewer.bboxCoord = new THREE.Box3(sceneBBoxWithoutAnchorPrev.min, sceneBBoxWithoutAnchorPrev.max);
			}

			var lengthX = this.ccAO.ccViewer.bboxCoord.max.x - this.ccAO.ccViewer.bboxCoord.min.x;
			var lengthY = this.ccAO.ccViewer.bboxCoord.max.y - this.ccAO.ccViewer.bboxCoord.min.y;
			var lengthZ = this.ccAO.ccViewer.bboxCoord.max.z - this.ccAO.ccViewer.bboxCoord.min.z;

			this.ccAO.ccViewer.sizeMax = lengthX;

			if(lengthY > this.ccAO.ccViewer.sizeMax) {
				this.ccAO.ccViewer.sizeMax = lengthY;
			} if(lengthZ > this.ccAO.ccViewer.sizeMax) {
				this.ccAO.ccViewer.sizeMax = lengthZ;
			}

			if(!isNaN(this.cameraOptions.cameraId)) {
				this.ccAO.ccViewer.setCameraToId(this.cameraOptions.cameraId, { animate: opts.animate });
			}

			if(this.cameraOptions.cameraPos) {
				this.ccAO.ccViewer.setCamera(this.cameraOptions.cameraPos.x, this.cameraOptions.cameraPos.y, this.cameraOptions.cameraPos.z);
			}

			if(this.cameraOptions.fitZoom) {
				this.ccAO.ccViewer.fitZoom(0.75, true);
			}
			
			if(!isNaN(this.cameraOptions.minZoom)) {
				this.ccAO.ccViewer.controls.minZoom = this.cameraOptions.minZoom;
			}
			
			if(!isNaN(this.cameraOptions.maxZoom)) {
				this.ccAO.ccViewer.controls.maxZoom = this.cameraOptions.maxZoom;
			}
			
			if(this.cameraOptions.target) {
				this.ccAO.ccViewer.controls.target.set(this.cameraOptions.target.x, this.cameraOptions.target.y, this.cameraOptions.target.z);
			}

			if(this.cameraOptions.maxZoom === "Infinity") {
				this.ccAO.ccViewer.controls.maxZoom = Infinity;
			}
		};
	}

	show() {
		this.hcs.api.instance().classList.remove("hcs-cad-hidden");
		this.autoResize();
	}

	hide() {
		this.hcs.api.instance().classList.add("hcs-cad-hidden");
	}

	/**
	 * Register event handler when scene size changes.
	 * @param {event} handler - Handler function being called after every scene resize.
	 * The event object contains the new scene dimensions like:
	 * {
	 *     width: xxx,
	 *     height: yyyy
	 * }
	 */
	sceneResized(handler) {
		this.sceneResizedEvents[this.sceneResizedEvents.length] = handler;
	}

	/**
	 * Register event handler when drag item state changes.
	 * This happens when
	 * - ccAO.anchorClick: Drag item has being placed
	 * - ccAO.noIntersectClick: User has clicked anywhere but not an anchor. Placing is aborted.
	 * - ccAO.resetInteraction: Placing aborted when user clicks somewhere outside of the configurator.
	 * This is a custom event and not directly triggered by ccAO.
	 * @param {event} handler - Handler function being called after drag item state changes.
	 * The event object contains the name of the ccAO event:
	 * {
	 *     eventType: xxx
	 * }
	 */
	dragItemStateChanged(handler) {
		this.dragItemStateChangedEvents[this.dragItemStateChangedEvents.length] = handler;
	}

	fireDragItemStateChanged(eventType) {
		this.dragItemStateChangedEvents.forEach(handler => {
			handler.call(self, {
				eventType: eventType
			});
		});
	}

	autoResize() {
		let self = this;

		if(this.ccAO === null || !this.initialized) {
			return;
		}

		this.ccAO.ccViewer.updateSize(this.webGlContainer.offsetWidth, this.webGlContainer.offsetHeight);

		this.sceneResizedEvents.forEach(handler => {
			handler.call(self, {
				width: self.webGlContainer.offsetWidth,
				height: self.webGlContainer.offsetHeight
			});
		});
	}
}
